import React, { useState } from "react";
import * as Yup from 'yup';
import _ from "lodash";
import {useLocation, useParams} from "react-router-dom";
import {PageHeader} from "components/molecules/PageHeader/PageHeader";
import {routes} from "routes";
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Typography from "@mui/material/Typography";
import {Card, FormControl, Grid, InputLabel, Select, FormHelperText} from "@mui/material";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import LinkIcon from '@mui/icons-material/Link';
import {Field, Form, Formik} from 'formik';
import {Select as SelectInput, TextField as FormikTextField} from 'formik-mui';
import {Box} from "@mui/system";
import {enqueueSnackbar} from 'notistack';
import {createLinkedAccount} from "redux/linkedAccount/linkedAccountSlice";
import {selectKYCStatus} from "redux/kyc/kycSlice";
import {useConfirmation} from "utils/ConfirmationProvider";


export default function BrokerAccountConnect() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirmation()
  const KYCStatus = useSelector(selectKYCStatus);
  
  const searchParams = new URLSearchParams(location.search);
  const broker = searchParams.get('broker');
  
  const [selectedBroker, setSelectedBroker] = useState(broker || '');

  const handleSubmit = (values, { setSubmitting }) => {
    if(_.isEmpty(KYCStatus) || KYCStatus.status !== "verified") {
      confirm({
        title: `KYC Verification Required`,
        message: <>Your KYC status is not VERIFIED. <br></br>Please request new KYC verification or check existing status. <br></br><br></br>You will be redirected to your Profile KYC status page.</>,
        onConfirm: () => {
          navigate(`${routes.account}?tab=kyc`);
        },
      });
      setSubmitting(false);
      return;
    }

    // Prepare the data
    const linkedAccountData = {
      broker: values.broker
    };

    // Add broker_data with account_number if it exists
    if (values.account_number) {
      linkedAccountData.broker_data = {
        account_number: values.account_number
      };
    }

    // Dispatch the action
    dispatch(createLinkedAccount(linkedAccountData))
      .unwrap()
      .then(() => {
        enqueueSnackbar(`Linked Account connected successfully.`, { variant: 'success' });
        navigate(routes.brokerAccountList);
      })
      .catch(() => {
        // Error handled by Redux
        setSubmitting(false);
      });
  };

  const initialValues = {
    broker: broker || '',
    account_number: ''
  };

  // Validation schema - handles both broker and account number validation
  const validationSchema = Yup.object().shape({
    broker: Yup.string().required('Broker is required'),
    account_number: Yup.string().when('broker', {
      is: 'interactive_brokers',
      then: () => Yup.string().required('Account number is required'),
      otherwise: () => Yup.string()
    })
  });

  // Handle broker selection change
  const handleBrokerChange = (event, setFieldValue) => {
    const brokerValue = event.target.value;
    setSelectedBroker(brokerValue);
    setFieldValue('broker', brokerValue);
  };

  return (
    <>
      <PageHeader text1={"Connect"} text2={"Broker Account"} description={"Connect new Broker Account"}/>

      <Grid container spacing={6}>
        <Grid item xs={12} sm={6}>
          <Card sx={{mt: 3, p: 3}}>
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({isSubmitting, values, setFieldValue, errors, touched}) => (
                <Form>
                  <FormControl fullWidth margin="dense" error={touched.broker && Boolean(errors.broker)}>
                    <Field
                      component={SelectInput}
                      id="broker"
                      name="broker"
                      label="Broker"
                      disabled={!!broker}
                      onChange={(e) => handleBrokerChange(e, setFieldValue)}
                      error={undefined}
                      helperText=""
                    >
                      <MenuItem value="interactive_brokers" selected>Interactive Brokers</MenuItem>
                    </Field>
                    {touched.broker && errors.broker && (
                      <FormHelperText error>{errors.broker}</FormHelperText>
                    )}
                  </FormControl>

                  {/* Display account number field only for Interactive Brokers */}
                  {(values.broker === 'interactive_brokers' || selectedBroker === 'interactive_brokers') && (
                    <Field
                      component={FormikTextField}
                      name="account_number"
                      label="Account Number"
                      fullWidth
                      margin="normal"
                      error={touched.account_number && Boolean(errors.account_number)}
                      helperText={
                        touched.account_number && errors.account_number
                          ? errors.account_number
                          : "Enter your Interactive Brokers account number"
                      }
                    />
                  )}

                  <Typography align="right">
                    <Button 
                      sx={{ mt: 3 }} 
                      type="submit" 
                      variant="contained" 
                      color="secondary" 
                      endIcon={<LinkIcon />}
                      disabled={isSubmitting}
                    > 
                      Connect 
                    </Button>
                  </Typography>
                </Form>
              )}
            </Formik>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box sx={{mt: 3}}>
            <Typography variant="h5">How to connect Broker Account?</Typography>
            <Typography component="p" sx={{mt: 2}}>
              To connect your broker account, follow these steps:
            </Typography>
            
            <Box component="ol" sx={{ mt: 1, pl: 2 }}>
              <Box component="li" sx={{ mt: 1 }}>
                <Typography component="p">
                  <strong>Select your broker</strong> from the dropdown menu.
                </Typography>
              </Box>
              
              <Box component="li" sx={{ mt: 1 }}>
                <Typography component="p">
                  <strong>Enter your account number</strong> - For Interactive Brokers, this can be found in your Account Management portal. The account number typically starts with a "U" followed by numbers (e.g., "U1234567").
                </Typography>
              </Box>
              
              <Box component="li" sx={{ mt: 1 }}>
                <Typography component="p">
                  <strong>Submit your connection request</strong> - Click the Connect button to submit your broker account for approval.
                </Typography>
              </Box>
              
              <Box component="li" sx={{ mt: 1 }}>
                <Typography component="p">
                  <strong>Wait for admin approval</strong> - After submission, your account will be in "Pending" status until an administrator approves the connection. This typically takes 1-2 business days.
                </Typography>
              </Box>
              
              <Box component="li" sx={{ mt: 1 }}>
                <Typography component="p">
                  <strong>Start using your connected account</strong> - Once approved, your broker account status will change to "Active," and you'll be able to view your balance and transaction data.
                </Typography>
              </Box>
            </Box>
            
            
          </Box>
        </Grid>

      </Grid>
    </>
  )
}